import { Client, ClientDto, clientFromDto } from '../client/client';
import { Clinic, ClinicDetailDto, DetailDtoToDomain } from '../clinic/clinic';

export const DefaultPagination = {
  pageNumber: 0,
  pageSize: 1,
  isAscending: false,
};

export const DefaultTestCategory = 'TEST_ALL';

export enum TestContext {
  READY = 'READY',
  ACTIVE = 'ACTIVE',
  DONE = 'DONE',
  ABORT = 'ABORT',
}

export interface TestSummaryDto {
  readonly id: Id;
  readonly name: Name;
  readonly serial: Serial;
  readonly testKind: TestKindDto;
  readonly testTatus: TestStatusDto;
  readonly patient: { name: Name };
  readonly testItemList: { testItem: { id: Id } }[];
  readonly createdAt: DateString;
}

export interface TestSummary {
  readonly id: Id;
  readonly serial: Serial;
  readonly name: Name;
  readonly clientName: Name;
  readonly testKind: TestKindDto;
  readonly testStatus: TestStatusDto;
  readonly numberOfItems: number;
  readonly createdAt: Date;
}
export interface TestKindDto {
  kind: TestKind;
  label: Label;
}
export interface TestStatusDto {
  status: TestStatus;
  label: Label;
  context: TestContext;
}
export interface TestCategory {
  category: Category;
  label: Label;
}

interface personnelInfo {
  id: Id;
  name: Name;
  phone: Phone;
}
export interface TestDto {
  readonly id: Id;
  readonly serial: Serial;
  readonly name: Name;
  readonly clinic?: ClinicDetailDto;
  readonly testKind: TestKindDto;
  readonly testTatus: TestStatusDto;
  readonly reason?: string;
  readonly personInCharge: personnelInfo;
  readonly registerClerk: personnelInfo;
  readonly testItemList: {
    testItem: TestItemSummary;
    testReport?: TestReport;
  }[];
  readonly patient: ClientDto;
  readonly createdAt: DateString;
  readonly expectedAt?: DateString;
  readonly canceledAt?: DateString;
  readonly testedAt?: DateString;
  readonly collectedAt?: DateString;
  readonly reportedAt?: DateString;
  readonly memo?: Memo;
  readonly organizationNumber?: string;
  readonly tester?: string;
  readonly checker?: string;
}

export interface TestItemSummary {
  id: Id;
  serial: Serial;
  name: Name;
  testCategory: TestCategory;
  requiredDays: Day;
  amount: Price;
  sampleCollectingFigure: String;
  specimen: String;
  shortInformation: String;
  longInformation: String;
  shortReferenceRange: String;
  longReferenceRange: String;
}

export interface TestReport {
  id: Id;
  sampleCount: Count;
  unit: Unit;
  result: TextResult;
  comment?: TextComment;
  imageUris: Image[];
}

export interface Test {
  readonly id: Id;
  readonly serial: Serial;
  readonly name: Name;
  readonly testKind: TestKindDto;
  readonly testStatus: TestStatusDto;
  readonly reason?: string;
  readonly clinic?: Clinic;
  readonly personInCharge: personnelInfo;
  readonly registerClerk: personnelInfo;
  readonly testItemList: {
    testItem: TestItemSummary;
    testReport?: TestReport;
  }[];
  readonly patient: Client;
  readonly createdAt: Date;
  readonly barcode?: Barcode;
  readonly canceledAt?: Date;
  readonly testedAt?: Date;
  readonly expectedAt?: Date;
  readonly collectedAt?: Date;
  readonly reportedAt?: Date;
  readonly memo: Memo;
  readonly organizationNumber?: string;
  readonly tester?: string;
  readonly checker?: string;
  readonly supporter?: string;
}

export interface TestCountResult {
  testStatus: TestStatusDto;
  totalElements: number;
}

export const testSummaryFromDto = (dto: TestSummaryDto): TestSummary => {
  return {
    ...dto,
    testStatus: dto.testTatus,
    createdAt: new Date(dto.createdAt),
    clientName: dto.patient?.name,
    numberOfItems: dto.testItemList.length,
  };
};

export const testFromDto = (dto: TestDto): Test => {
  return {
    ...dto,
    clinic: dto.clinic ? DetailDtoToDomain(dto.clinic) : undefined,
    testStatus: dto.testTatus,
    patient: clientFromDto(dto.patient),
    createdAt: new Date(dto.createdAt),
    expectedAt:
      dto.expectedAt === undefined || dto.expectedAt === null
        ? undefined
        : new Date(dto.expectedAt),
    collectedAt:
      dto.collectedAt === undefined || dto.collectedAt === null
        ? undefined
        : new Date(dto.collectedAt),
    reportedAt:
      dto.reportedAt === undefined || dto.reportedAt === null
        ? undefined
        : new Date(dto.reportedAt),
    canceledAt:
      dto.canceledAt === undefined || dto.canceledAt === null
        ? undefined
        : new Date(dto.canceledAt),
    testedAt:
      dto.testedAt === undefined || dto.testedAt === null
        ? undefined
        : new Date(dto.testedAt),
    memo: dto.memo === undefined || dto.memo === null ? '' : dto.memo,
  };
};
